/**
 * 216 (4) (DIRECTION §0.12; GameConfig.roads): a match's roads on a planet — the planet's settlements (core/settlements.ts)
 * and the sides' bases, joined by the route graph's roads (core/planetRoutes.ts planetRoads). Null with the switch off, or
 * fewer than two ends. Pure.
 */
import type { GameState, PlanetState, Vec3 } from '../types';
import { planetRoads, planetRouteGraph, type Roads, type RoadWorld } from './planetRoutes';
import { planetSettlements, SETTLEMENT_WORLD, type SettlementSite } from './settlements';
import { worldSheetOf, WORLD_SHEET, type WorldSheet } from './worldSheet';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { dacos, dpow } from './dmath';

/**
 * 74.5 step 2 (DECISIONS 357, ARCHITECTURE §5.41 (3)): the roads along the world. `on` off: the roads as before 74.5, bit
 * for bit (the A/B's before arm).
 */
export const ROADS_WORLD = {
  on: true,
  /** A trail runs to a ridge post this near (people) at most — past it the post is another settlement's, not a walk from this one. */
  trailPostPeople: 600,
};

const worlds = new WeakMap<WorldSheet, RoadWorld & { fords: number[]; lake: Uint8Array }>();
/**
 * 74.5: a planet's world rules for wheels, from its world sheet (cached per sheet): a chain's vertices where it runs strong
 * (the flow there of order WORLD_SHEET.fordOrder and up — the sheet's doublings over its threshold, so a river's head is
 * not, and a road may go round it) refused but at the fords (the sheet's ford candidates), a playa's floor firm, the strong
 * stretches' banks (a vertex beside one, not on it) cheap to lay a road along, a lake's floor dear to lay one across. A lake
 * is not refused: no water is drawn on it yet (74.3 step 2), and a base may stand on its floor (story-5, -11, -12).
 */
export function roadWorld(sheet: WorldSheet): RoadWorld & { fords: number[]; lake: Uint8Array } {
  const hit = worlds.get(sheet);
  if (hit) return hit;
  const g = sheet.graph, V = g.count;
  const refuse = new Uint8Array(V), firm = new Uint8Array(V), bank = new Uint8Array(V), strong = new Uint8Array(V);
  const strongFlow = WORLD_SHEET.flowFrom * dpow(2, WORLD_SHEET.fordOrder * WORLD_SHEET.flowDoublings);   // (the order's own scale: doublings over the threshold, flow counting vertices)
  for (const c of sheet.chains) if (c.order >= WORLD_SHEET.fordOrder) for (const v of c.vertices) if (sheet.flow[v] >= strongFlow) strong[v] = 1;
  const lake = new Uint8Array(V);
  for (const b of sheet.basins) {
    if (!b.lake && !b.playa) continue;
    for (const v of b.vertices) if (sheet.radius[v] < b.spillRadius) { if (b.lake) lake[v] = 1; else firm[v] = 1; }
  }
  const fords = sheet.candidates.filter(c => c.kind === 'ford').map(c => c.vertex).sort((a, b) => a - b);
  for (let v = 0; v < V; v++) if (strong[v]) refuse[v] = 1;
  for (const v of fords) refuse[v] = 0;
  for (let v = 0; v < V; v++) {
    if (strong[v]) continue;
    for (let k = g.start[v]; k < g.start[v + 1]; k++) if (strong[g.nbr[k]]) { bank[v] = 1; break; }
  }
  const w = { key: `w${fords.join(',')}`, refuse, firm, bank, lake, fords };
  worlds.set(sheet, w);
  return w;
}

const arcOf = (a: Vec3, b: Vec3) => dacos(Math.max(-1, Math.min(1, (a.x * b.x + a.y * b.y + a.z * b.z) / Math.sqrt((a.x * a.x + a.y * a.y + a.z * a.z) * (b.x * b.x + b.y * b.y + b.z * b.z)))));

export function roadsOf(state: GameState, planetIndex: number): Roads | null {
  if (!state.config.roads) return null;
  const planet = state.planets[planetIndex];
  if (!planet) return null;
  const sites = planetSettlements(state, planetIndex);
  const ends: Vec3[] = sites.map(s => s.dir);
  for (const b of state.board?.bases ?? []) if (b.planetIndex === planetIndex) ends.push(b.dir);
  if (!state.board) for (const d of Object.values(state.actionHomes ?? {})) ends.push(d);
  if (!ROADS_WORLD.on || planet.type !== 'planet') return planetRoads(planet, ends);
  // 74.5: laid under the world's rules, and the trails — each settlement to its nearest stream (a chain's vertex, of the
  // settlements' water order and up) and to the nearest ridge post within ROADS_WORLD.trailPostPeople
  const sheet = worldSheetOf(planet);
  return planetRoads(planet, ends, roadWorld(sheet), trailsOf(sheet, sites, planet.radius, PERSON_BODY_HEIGHT * personSetOf(state).k));
}

const trailMemo = new WeakMap<WorldSheet, WeakMap<readonly SettlementSite[], Map<number, Array<[Vec3, Vec3]>>>>();
/** The trails' ends for `sites` (planetSettlements' cached list) on `sheet`, memoised: roadsOf runs per truck per tick. */
function trailsOf(sheet: WorldSheet, sites: readonly SettlementSite[], radius: number, person: number): Array<[Vec3, Vec3]> {
  let bySites = trailMemo.get(sheet);
  if (!bySites) { bySites = new WeakMap(); trailMemo.set(sheet, bySites); }
  let byPerson = bySites.get(sites);
  if (!byPerson) { byPerson = new Map(); bySites.set(sites, byPerson); }
  const hit = byPerson.get(radius / person);
  if (hit) return hit;
  const g = sheet.graph;
  const water: Vec3[] = [];
  for (const c of sheet.chains) if (c.order >= SETTLEMENT_WORLD.waterOrder) for (const v of c.vertices) water.push({ x: g.dirs[3 * v], y: g.dirs[3 * v + 1], z: g.dirs[3 * v + 2] });
  const posts = sites.filter(s => s.kind === 'ridgePost').map(q => q.dir);
  const trails: Array<[Vec3, Vec3]> = [];
  const nearest = (from: Vec3, list: readonly Vec3[]): Vec3 | null => { let best: Vec3 | null = null, d = Infinity; for (const p of list) { const a = arcOf(from, p); if (a < d) { d = a; best = p; } } return best; };
  for (const s of sites) {
    const w = nearest(s.dir, water);
    if (w) trails.push([s.dir, w]);
    if (s.kind !== 'ridgePost') { const p = nearest(s.dir, posts); if (p && arcOf(s.dir, p) * radius <= ROADS_WORLD.trailPostPeople * person) trails.push([s.dir, p]); }
  }
  byPerson.set(radius / person, trails);
  return trails;
}

const cutMemo = new WeakMap<Roads, Map<string, Roads>>();
/**
 * 74.5 (357, "the ford as the objective: a crossing held cuts the road for convoys"): `team`'s roads on a planet — its
 * roads, less every ford where an objective another side holds stands (within SETTLEMENT_WORLD.kindCrossingPeople: a ford
 * village's own), refused to its wheels. The same roads with none held, or without the world. Memoised per network and cut.
 */
export function roadsFor(state: GameState, planetIndex: number, team: number): Roads | null {
  const roads = roadsOf(state, planetIndex);
  if (!roads?.world) return roads;
  const planet = state.planets[planetIndex];
  const held = (state.modeState?.points?.sites ?? []).filter(s => s.planetIndex === planetIndex && s.owner !== null && s.owner !== team);
  if (!held.length) return roads;
  const sheet = worldSheetOf(planet), g = sheet.graph, fords = roadWorld(sheet).fords;
  const reach = (SETTLEMENT_WORLD.kindCrossingPeople * PERSON_BODY_HEIGHT * personSetOf(state).k) / planet.radius;
  const cut = fords.filter(v => { const d = { x: g.dirs[3 * v], y: g.dirs[3 * v + 1], z: g.dirs[3 * v + 2] }; return held.some(s => arcOf(s.surfaceDir, d) <= reach); });
  if (!cut.length) return roads;
  let memo = cutMemo.get(roads);
  if (!memo) { memo = new Map(); cutMemo.set(roads, memo); }
  const key = cut.join(',');
  const hit = memo.get(key);
  if (hit) return hit;
  const refuse = Uint8Array.from(roads.world.refuse);
  for (const v of cut) refuse[v] = 1;
  const out: Roads = { ...roads, key: `${roads.key}|cut${key}`, world: { ...roads.world, key: `${roads.world.key}|cut${key}`, refuse } };
  memo.set(key, out);
  return out;
}

const linesMemo = { roads: new WeakMap<Roads, Vec3[][]>(), trails: new WeakMap<Roads, Vec3[][]>() };

/**
 * The roads as lines for the planet table (68.42): `roads`' edges chained into polylines of vertex directions, each run
 * from an end or a fork to the next (a loop from its lowest vertex), in vertex order. Pure, memoised per network. 74.5:
 * `which` 'trails' chains its trails' edges instead.
 */
export function roadLines(planet: PlanetState, roads: Roads, which: 'roads' | 'trails' = 'roads'): Vec3[][] {
  const hit = linesMemo[which].get(roads);
  if (hit) return hit;
  const g = planetRouteGraph(planet), n = g.count;
  const adj = new Map<number, number[]>();
  const link = (a: number, b: number) => { const l = adj.get(a); if (l) l.push(b); else adj.set(a, [b]); };
  for (const k of (which === 'trails' ? roads.trails : roads.edges) ?? []) { const a = Math.floor(k / n), b = k - a * n; link(a, b); link(b, a); }
  for (const l of adj.values()) l.sort((a, b) => a - b);
  const used = new Set<number>(), key = (a: number, b: number) => a < b ? a * n + b : b * n + a;
  const dir = (v: number): Vec3 => ({ x: g.dirs[3 * v], y: g.dirs[3 * v + 1], z: g.dirs[3 * v + 2] });
  const out: Vec3[][] = [];
  const walk = (from: number, to: number) => {
    const line = [dir(from)];
    let prev = from, v = to;
    used.add(key(from, to));
    for (;;) {
      line.push(dir(v));
      const nb = adj.get(v)!;
      if (nb.length !== 2) break;
      const next = nb[0] === prev ? nb[1] : nb[0];
      if (used.has(key(v, next))) break;
      used.add(key(v, next));
      prev = v; v = next;
    }
    out.push(line);
  };
  const verts = [...adj.keys()].sort((a, b) => a - b);
  for (const pass of [0, 1]) for (const v of verts) {   // ends and forks first, then what is left (loops)
    if (pass === 0 && adj.get(v)!.length === 2) continue;
    for (const w of adj.get(v)!) if (!used.has(key(v, w))) walk(v, w);
  }
  linesMemo[which].set(roads, out);
  return out;
}
